import React, { useId } from 'react';
import { Button, Dropdown, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';

import ToggleRow from '../common/ToggleRow';
import ChannelBank from './ChannelBank';
import SourceMark from './SourceMark';
import TermText from './TermText';
import UserAvatar from '../users/UserAvatar';
import { buildActionMenu } from '../common/actionMenu';
import { ARROW_COUNTERCLOCKWISE, COLLECTION_ICON, DUPLICATE, EDIT, KEBAB, LOCK, SLASH_CIRCLE } from '../icons';
import { joinMeta } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import {
	describeAudience,
	describeDelivery,
	describeEscalation,
	describeMute,
	describeResetTarget,
	describeSource,
	describeTrigger,
	hasOwnSettings,
} from '../../utils/notificationCopy';

// Four faces, then a count: enough to recognise a team, short enough to
// leave the audience sentence beside it room to be read.
const MAX_FACES = 4;

/**
 * One notification, at whichever tier you are standing on.
 *
 * It is a `ToggleRow` whose `children` replace the switch slot — the row IS
 * a boolean setting, it just has three of them — so it keeps `.toggle-row`
 * anatomy and the `--line-soft` divider every settings list in the product
 * is already divided by. The grid is
 * `minmax(0,1fr) 104px auto 32px`: the lead takes the slack, the Set by
 * column is fixed so the marks form a column, the bank and the ⋯ hold their
 * squares and never give way (the name truncates instead).
 *
 * The lead is three lines, and they answer the three questions a reader
 * arrives with in order: WHAT is this (the name, plus the tags that change
 * what the row can do), WHEN does it fire, WHO does it reach. Lines two and
 * three are generated from the row (`utils/notificationCopy`) and never
 * typed here, so a notification that changes cannot leave a stale sentence
 * behind it in a list somebody is reading.
 */
export default function NotificationRow({
	row,
	// This row's identity WITHIN the list — its group and its key. The key
	// alone is not one: the account catalog returns the same key once per
	// collection (see `NotificationList` § rowId).
	rowId = null,
	tier,
	channels,
	usersById = {},
	onToggle,
	onMute,
	onReset,
	busyChannel = null,
	readOnly = false,
	compact = false,
	first = false,
	unavailableHintId,
	readOnlyHintId,
	flash = false,
	collectionId = null,
	collectionName = null,
}) {
	const generatedId = useId();
	const navigate = useNavigate();
	const lockReasonId = `notification-lock-${generatedId}`;

	const escalation = describeEscalation(row);
	const trigger = describeTrigger(row);
	const source = describeSource(row, tier);
	const mute = describeMute(row);
	const ownHere = hasOwnSettings(row, tier);
	const resetTarget = describeResetTarget(tier, { hasCollection: Boolean(collectionId) });

	// On a phone the Set by column folds into line three as one more fact —
	// the same answer, in the same order, just travelling with the audience
	// instead of standing in a column of its own. It folds as the SENTENCE
	// (`phrase`), never the column word: out of its column, "Mine" and
	// "Required" stop being answers to *Set by*.
	const audienceLine = joinMeta(
		describeAudience(row),
		describeDelivery(row),
		mute ? `${mute} — it still reaches your inbox` : null,
		compact ? source.phrase : null
	);

	const faces = (row?.audience?.userIds || []).slice(0, MAX_FACES);
	const overflow = (row?.audience?.userIds || []).length - faces.length;

	const actions = [
		// The door to the editor. A row you may author opens it; a row that
		// fell through from a tier above offers to FORK it at this one, which
		// is what `?from=` seeds — the override keeps the key it overrides,
		// because at this tier the key IS the override (Decision 2).
		collectionId && row?.canEdit && row?.automation_id ? {
			key: 'edit',
			group: 'navigate',
			icon: <EDIT />,
			label: 'Edit notification…',
			onClick: () => navigate(`/collections/${collectionId}/notifications/${normalizeId(row.automation_id)}/edit`),
		} : null,
		collectionId && ! row?.canEdit && tier === 'collection' && row?.definedAt === 'global' ? {
			key: 'override',
			group: 'record',
			icon: <DUPLICATE />,
			label: 'Override for this collection…',
			onClick: () => navigate(`/collections/${collectionId}/notifications/new?from=${encodeURIComponent(row.key)}`),
		} : null,
		collectionId && tier !== 'collection' ? {
			key: 'collection',
			group: 'navigate',
			icon: <COLLECTION_ICON />,
			label: collectionName ? `Go to ${collectionName}` : 'Go to collection',
			onClick: () => navigate(`/collections/${collectionId}`),
		} : null,
		{
			key: 'mute',
			group: 'record',
			icon: <SLASH_CIRCLE />,
			label: row?.muted ? 'Unmute' : 'Mute',
			// Never a bare disabled entry: each one says why it is off, and
			// the live one says what muting does NOT stop.
			title: readOnly
				? 'This list is read-only for you.'
				: (row?.muted ? null : 'Stops the email, text and push. The inbox row is still written.'),
			disabled: readOnly,
			onClick: () => onMute(!row?.muted),
		},
		{
			key: 'reset',
			group: 'record',
			icon: <ARROW_COUNTERCLOCKWISE />,
			label: `Reset to ${resetTarget}`,
			title: readOnly
				? 'This list is read-only for you.'
				: (ownHere ? null : 'Nothing is set here, so there is nothing to reset.'),
			disabled: readOnly || !ownHere,
			// The row knows where its own value falls back to (an account row
			// with no collection skips a tier); the list says so out loud.
			onClick: () => onReset(resetTarget),
		},
	].filter(Boolean);

	const menu = buildActionMenu(actions);

	const classes = [
		'notification-row',
		row?.muted ? 'notification-row--muted' : '',
		// A deep link from the inbox drawer landed on this row.
		flash ? 'notification-row--flash' : '',
	].filter(Boolean).join(' ');

	const label = (
		<>
			<span className="notification-row__name">{row?.name}</span>
			{source.locked ? (
				<Tag className="notification-tag" icon={<LOCK aria-hidden="true" />}>Required</Tag>
			) : null}
			{escalation ? <Tag className="notification-tag">{escalation}</Tag> : null}
		</>
	);

	const description = (
		<>
			{trigger ? (
				<span className="notification-row__trigger">
					<TermText text={trigger} />
				</span>
			) : null}
			<span className="notification-row__audience">
				{faces.length ? (
					// The names are already in the sentence beside it; the stack
					// is a glance, not a second reading of the same fact.
					<span className="notification-row__faces" aria-hidden="true">
						{faces.map(id => (
							<span key={id} className="notification-row__face" title={usersById[id]?.fullName || undefined}>
								<UserAvatar user={usersById[id]} size={20} />
							</span>
						))}
						{overflow > 0 ? <span className="notification-row__face-more">+{overflow}</span> : null}
					</span>
				) : null}
				{/* The audience summary is the API's sentence too (`describe.js`
				    marks the element a `{type:'element'}` audience names), so it
				    reads through the same convention as the trigger line above —
				    rendered verbatim it would show a reader literal asterisks. */}
				{audienceLine ? (
					<span className="notification-row__audience-text"><TermText text={audienceLine} /></span>
				) : null}
			</span>
			{source.locked && row?.lockReason ? (
				// Read out by every forced cell through aria-describedby: the
				// reason belongs to the row, and is said once inside it.
				<span id={lockReasonId} className="visually-hidden">{row.lockReason}</span>
			) : null}
		</>
	);

	return (
		<li className="notification-list__item" data-notification-key={row?.key} data-notification-row={rowId || undefined}>
			<ToggleRow className={classes} first={first} label={label} description={description}>
				{compact ? null : <SourceMark row={row} tier={tier} />}
				<ChannelBank
					row={row}
					channels={channels}
					onToggle={onToggle}
					busyChannel={busyChannel}
					readOnly={readOnly}
					unavailableHintId={unavailableHintId}
					readOnlyHintId={readOnlyHintId}
					lockReasonId={source.locked && row?.lockReason ? lockReasonId : undefined}
				/>
				{menu ? (
					<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
						<Button icon={<KEBAB />} aria-label={`Actions for ${row?.name || 'this notification'}`} />
					</Dropdown>
				) : <span />}
			</ToggleRow>
		</li>
	);
}
